<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type {
  PublicVolunteerInfo,
  PublicVolunteerSlot,
  PublicVolunteerRole,
  VolunteerQualificationPayload,
} from '@/types/volunteer'
import VolunteerQualificationsBlock from '@/components/volunteer/VolunteerQualificationsBlock.vue'

const props = defineProps<{
  info: PublicVolunteerInfo
  formData: {
    first_name: string
    last_name: string
    email: string
    phone: string
    emergency_contact_name: string
    emergency_contact_phone: string
  }
  selectedSlotIds: number[]
  selectedRoleIds: number[]
  qualificationAnswers: VolunteerQualificationPayload[]
  loading: boolean
  errors: Record<string, string[]>
}>()

const emit = defineEmits<{
  'update:qualificationAnswers': [answers: VolunteerQualificationPayload[]]
  'submit-clicked': []
  'back-clicked': []
}>()

const { t } = useI18n()

function formatTime(time: string): string {
  return time.length > 5 ? time.slice(0, 5) : time
}

function getSelectedSlots(): PublicVolunteerSlot[] {
  return props.info.slots.filter((s) => props.selectedSlotIds.includes(s.id))
}

function getSelectedRoles(): PublicVolunteerRole[] {
  return props.info.roles.filter((r) => props.selectedRoleIds.includes(r.id))
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}
</script>

<template>
  <div class="space-y-6">
    <h2 class="text-lg font-bold text-slate-900">{{ t('volunteer.wizard.step3.title') }}</h2>

    <!-- Summary card -->
    <div class="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-3">
      <h3 class="text-sm font-semibold text-sky-800">{{ t('volunteer.wizard.step3.summary_heading') }}</h3>

      <!-- Details -->
      <div class="text-sm text-sky-900">
        <p>{{ formData.first_name }} {{ formData.last_name }}</p>
        <p class="text-sky-700">{{ formData.email }}</p>
        <p class="text-sky-700">{{ formData.phone }}</p>
      </div>

      <!-- Selected slots -->
      <div v-if="getSelectedSlots().length > 0">
        <p class="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">
          {{ t('volunteer.wizard.step2.slot_heading') }}
        </p>
        <ul class="space-y-0.5">
          <li
            v-for="slot in getSelectedSlots()"
            :key="slot.id"
            class="text-sm text-sky-900"
          >
            {{ formatDate(slot.slot_date) }} · {{ formatTime(slot.start_time) }}–{{ formatTime(slot.end_time) }}
          </li>
        </ul>
      </div>

      <!-- Selected roles -->
      <div v-if="getSelectedRoles().length > 0">
        <p class="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">
          {{ t('volunteer.wizard.step2.role_heading') }}
        </p>
        <ul class="space-y-0.5">
          <li
            v-for="role in getSelectedRoles()"
            :key="role.id"
            class="text-sm text-sky-900"
          >
            {{ role.name }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Qualifications -->
    <div v-if="info.qualifications.length > 0">
      <VolunteerQualificationsBlock
        :qualifications="info.qualifications"
        :model-value="qualificationAnswers"
        @update:model-value="emit('update:qualificationAnswers', $event)"
      />
    </div>

    <!-- General error -->
    <div
      v-if="errors['_general']"
      class="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
    >
      {{ errors['_general'][0] }}
    </div>

    <!-- Submit thanks line -->
    <p class="text-sm text-slate-500">{{ t('volunteer.wizard.step3.thanks_line') }}</p>

    <!-- Navigation -->
    <div class="flex gap-3 pt-2">
      <button
        type="button"
        :disabled="loading"
        class="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors"
        @click="emit('back-clicked')"
      >
        {{ t('common.back') }}
      </button>
      <button
        type="button"
        :disabled="loading"
        class="flex-1 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        @click="emit('submit-clicked')"
      >
        <span v-if="loading" class="inline-flex items-center gap-2">
          <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {{ t('common.submitting') }}
        </span>
        <span v-else>{{ t('volunteer.wizard.step3.submit') }}</span>
      </button>
    </div>
  </div>
</template>
